import React, { useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  Grid,
} from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { AppDispatch, RootState } from "../../../redux/store";
import { fetchAssessedSession } from "../../../redux/actions/assessmentActions";
import { clearCurrentAssessedSession } from "../../../redux/reducers/assessmentReducer";
import AssessedSessionMetadataCard from "./AssessedSessionMetadataCard";
import AssessedSessionMemoryView from "./AssessedSessionMemoryView";
import AssessedSessionFormView from "./AssessedSessionFormView";
import AssessedSessionRoleplayView from "./AssessedSessionRoleplayView";

const AssessedSessionView: React.FC = () => {
  const { assessmentId, sessionId } = useParams<{
    assessmentId: string;
    sessionId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  const { currentAssessedSession, sessionsLoading, sessionsError } =
    useSelector((state: RootState) => state.assessments);

  // Get organization ID from query params
  const queryParams = new URLSearchParams(location.search);
  const organizationId = queryParams.get("org");

  useEffect(() => {
    if (sessionId && organizationId) {
      dispatch(fetchAssessedSession({ sessionId, organizationId }));
    }
    return () => {
      dispatch(clearCurrentAssessedSession());
    };
  }, [dispatch, sessionId, organizationId]);

  if (sessionsLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (sessionsError) {
    return (
      <Box p={3}>
        <Typography color="error">Error: {sessionsError}</Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() =>
            navigate(
              `/dashboard/assessments/${organizationId}/${assessmentId}`
            )
          }
          sx={{ mt: 2 }}
        >
          Back to Assessment
        </Button>
      </Box>
    );
  }

  if (!currentAssessedSession) {
    return (
      <Box p={3}>
        <Typography>Assessed session not found</Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() =>
            navigate(
              `/dashboard/assessments/${organizationId}/${assessmentId}`
            )
          }
          sx={{ mt: 2 }}
        >
          Back to Assessment
        </Button>
      </Box>
    );
  }

  // Determine which view to render based on processing_type
  const processingType = currentAssessedSession.processing_type;
  const isFormView = processingType === "form";
  const isRoleplayView = processingType === "roleplay";

  // Determine back navigation based on source
  const getBackNavigation = () => {
    if (currentAssessedSession.source_roleplay_id) {
      return `/dashboard/roleplays/${organizationId}/${currentAssessedSession.source_roleplay_id}`;
    }
    return `/dashboard/assessments/${organizationId}/${assessmentId}`;
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate(getBackNavigation())}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">
            {isRoleplayView ? "Roleplay" : "Assessment"} Session Details
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Common Metadata Card */}
        <Grid item xs={12}>
          <AssessedSessionMetadataCard
            currentAssessedSession={currentAssessedSession}
            sessionId={sessionId}
          />
        </Grid>

        {/* Render appropriate view based on processing_type */}
        <Grid item xs={12}>
          {isRoleplayView ? (
            <AssessedSessionRoleplayView
              currentAssessedSession={currentAssessedSession}
            />
          ) : isFormView ? (
            <AssessedSessionFormView
              currentAssessedSession={currentAssessedSession}
            />
          ) : (
            <AssessedSessionMemoryView
              currentAssessedSession={currentAssessedSession}
            />
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default AssessedSessionView;
